import { Check, XCircle } from 'lucide-react';
import {
    PIPELINE_STAGE_ORDER, PIPELINE_STAGE_LABELS, PIPELINE_STAGE_PIVOT,
    isTerminalStage, stageRank,
} from './pipelineStages';
import { SURTITRE } from '../ui/pills';
import type { Dossier } from './SuiviPrivatisationLayout';

const fmtCourt = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }); } catch { return ''; }
};

/** Jours entiers entre aujourd'hui et le service ; null sans date. */
const joursAvant = (d?: string | null): number | null => {
    if (!d) return null;
    const t = new Date(d).getTime();
    if (isNaN(t)) return null;
    return Math.ceil((t - Date.now()) / 86400000);
};

/**
 * Avancement du dossier (maquette 18a) : une carte par étape, en ligne et à la suite, avec son numéro,
 * sa pastille (cochée quand franchie), son libellé et la date réelle du franchissement — lue dans le
 * journal du dossier, jamais recalculée. L'étape en cours porte le jeton « attention » : c'est là que
 * le regard doit aller.
 *
 * Les douze étapes sont celles du registre `pipelineStages.ts` (arbitrées par le serveur) — la maquette
 * en dessinait huit sur un parcours imaginé, l'appli garde le sien.
 */
export function PipelineStepperBar({ dossier }: { dossier: Dossier }) {
    const stage = dossier.stage;
    const courant = stageRank(stage);
    const abandonne = isTerminalStage(stage);

    // Date à laquelle chaque étape a été atteinte : le dernier passage du journal vers cette étape.
    const atteinteLe: Record<string, string> = {};
    dossier.journal.forEach((j) => { if (j.to_stage) atteinteLe[j.to_stage] = j.changed_at; });
    if (dossier.journal[0] && !atteinteLe.demande_recue) atteinteLe.demande_recue = dossier.journal[0].changed_at;

    const jours = joursAvant(dossier.event?.start_time);
    const compteur = abandonne
        ? `${PIPELINE_STAGE_LABELS[stage]} — le parcours s'arrête ici`
        : `Étape ${courant + 1} sur ${PIPELINE_STAGE_ORDER.length}${jours !== null ? ` · ${jours > 0 ? `${jours} jour${jours > 1 ? 's' : ''} avant le service` : jours === 0 ? "c'est aujourd'hui" : 'service passé'}` : ''}`;

    return (
        <div className="px-5 py-4 bg-surface border-b border-border">
            <div className="flex items-baseline justify-between gap-2.5 flex-wrap mb-3">
                <span className={SURTITRE}>Avancement du dossier</span>
                <span className={`text-[11.5px] ${abandonne ? 'text-state-danger font-semibold inline-flex items-center gap-1.5' : 'text-content-muted'}`}>
                    {abandonne && <XCircle size={13} />}{compteur}
                </span>
            </div>
            <ol className="flex items-stretch gap-1.5 flex-wrap">
                {PIPELINE_STAGE_ORDER.map((s, i) => {
                    // Sur un dossier abandonné, on fige la frise là où il en était : tout marquer comme non
                    // atteint effacerait le chemin déjà parcouru, qui est justement ce qu'on vient lire.
                    const franchi = !abandonne && i < courant;
                    const actif = !abandonne && i === courant;
                    const pivot = s === PIPELINE_STAGE_PIVOT;
                    const meta = actif
                        ? (dossier.stage_since ? `depuis le ${fmtCourt(dossier.stage_since)}` : 'en cours')
                        : franchi ? fmtCourt(atteinteLe[s]) || 'passée' : pivot ? 'jalon' : '—';
                    return (
                        <li key={s}
                            className={`flex-[1_1_108px] min-w-[108px] px-2.5 py-[9px] rounded-[9px] border ${
                                actif ? 'bg-state-warn-soft border-state-warn/30' : 'bg-surface-soft border-border-soft'}`}>
                            <div className="flex items-center gap-1.5">
                                <span className={`w-3.5 h-3.5 shrink-0 rounded-full flex items-center justify-center border-[1.5px] ${
                                    franchi ? 'bg-state-ok border-state-ok text-surface'
                                        : actif ? 'bg-state-warn border-state-warn'
                                            : 'bg-surface border-border'}`}>
                                    {franchi && <Check size={8} strokeWidth={4} />}
                                </span>
                                <span className={`font-mono text-[9.5px] tabular-nums ${actif ? 'text-state-warn' : 'text-content-muted'}`}>
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                            </div>
                            <div className={`mt-1.5 text-[11.5px] leading-[1.3] text-pretty ${
                                actif ? 'font-bold text-state-warn' : franchi ? 'font-semibold text-content' : 'font-semibold text-content-muted'}`}>
                                {PIPELINE_STAGE_LABELS[s]}
                            </div>
                            <div className={`mt-[3px] text-[10px] tabular-nums ${actif ? 'text-state-warn' : 'text-content-muted'}`}>{meta}</div>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}
